import { EnergyType, FilterSortProducers, ProducerLocation, CapacityState, Producer, State } from 'types/producer'
import { CookieKeys } from 'types/cookies'
import { readCookie } from 'utils/cookies'
import { PopulatedLocation } from 'components/producers/new-producer-map/types'
import { InputGroups } from 'store/customer/types'
import { InputGroupKeys } from 'store/customer/enums'
import { AVERAGE_CAPACITY_USAGE } from 'constants/usages'
import { stringify } from 'qs'
import axios from 'axios'
import { NextApiResponseBody } from 'types/bolt-api/request'
import { getTotalElectricityVolumeForMeterType } from 'utils/usage'
import { keyFromSelector, type SelectorKey } from 'i18next'

/**
 * Fetches the capacity state for a given producer
 *
 * @param {PopulatedLocation} producer
 * @param {InputGroups[InputGroupKeys.SIMULATION]} simulationInputs
 * @param {boolean} didSimulation
 * @returns {CapacityState}
 */
export const getCapacityState = (
  producer: PopulatedLocation,
  simulationInputs: InputGroups[InputGroupKeys.SIMULATION],
  didSimulation: boolean
): CapacityState => {
  const usedEnergy = producer.currentCapacity > 100 ? 100 : Math.round(producer.currentCapacity)

  // If producer is marked as sold out in CMS
  if (producer.state === State.SOLD_OUT || usedEnergy >= 100) {
    return { almostSoldOut: true, soldOut: true, usedEnergy: 100 }
  }

  // If producer is marked as reserved in CMS
  // (unless user has unlocked producer using ?unlock=id)
  if (isReserved(producer)) {
    return { almostSoldOut: false, soldOut: false, usedEnergy: 100 }
  }

  // If user did no simulation, assume average usage of 3500 kWh (also max usage for express registration)
  let volume = AVERAGE_CAPACITY_USAGE
  if (didSimulation) {
    // If user did simulation, take sum of usages based on meter type
    volume = getTotalElectricityVolumeForMeterType(simulationInputs.meterType, simulationInputs.usage)
  }

  const used = Math.round(((producer.energyUsed + volume) / producer.maxCapacity) * 100)

  // Use minimum percentage from CMS for display
  // We don't want to show 0%
  let visiblePercentage = producer.minOccupation
  if (used > producer.minOccupation) {
    // If the actual used capacity is higher than the CMS value, use the calculated one
    visiblePercentage = used
  }
  // User is allowed to sign up if less than 100% used
  // Make sure the visible percentage is not above 100
  return {
    almostSoldOut: used >= 80,
    soldOut: used >= 100,
    usedEnergy: Number(Math.min(visiblePercentage, 100).toFixed(0))
  }
}

/**
 * Returns the `common` namespace i18n key for given energy type
 *
 * @param {`${EnergyType}`} energyType
 * @returns {SelectorKey}
 */
export const getEnergyNameI18Key = (energyType: `${EnergyType}`): SelectorKey => {
  const keys: Record<`${EnergyType}`, SelectorKey> = {
    [EnergyType.SOLAR]: keyFromSelector(($) => $.energyType.Solar),
    [EnergyType.WATER]: keyFromSelector(($) => $.energyType.Water),
    [EnergyType.WIND]: keyFromSelector(($) => $.energyType.Wind),
    [EnergyType.BIO]: keyFromSelector(($) => $.energyType.BioEnergy)
  }

  return keys[energyType]
}

/**
 * Returns the icon based on the energy type
 *
 * @param {EnergyType} energyType
 * @returns {'leaf' | 'sun' | 'water' | 'wind'}
 */
export const getEnergyTypeIcon = (energyType: EnergyType): 'leaf' | 'sun' | 'water' | 'wind' => {
  switch (energyType) {
    case EnergyType.BIO:
      return 'leaf'

    case EnergyType.SOLAR:
      return 'sun'

    case EnergyType.WATER:
      return 'water'

    case EnergyType.WIND:
      return 'wind'
  }
}

/**
 * Helper function to fetch & filter producers
 *
 * @param {FetchProducersQuery} filters
 * @returns {Promise<Producer[]>}
 */
export const getProducersApiHelper = async ({ language, params }): Promise<Producer[]> => {
  const query = stringify(params, { addQueryPrefix: true, skipNulls: true })
  const {
    data: { data }
  } = await axios.get<NextApiResponseBody<Producer[]>>(`/api/producers${query}`)

  return data ? filterAndSortProducers({ hideSoldOut: params?.soldOut === false, language, producers: data }) : []
}

/**
 * Checks if a given producer is reserved
 *
 * @param {PopulatedLocation} producer
 * @returns boolean
 */
export const isReserved = (producer: PopulatedLocation): boolean => {
  if (!producer.state || producer.state !== State.RESERVED) {
    return false
  }

  const unlockedProducerId = readCookie(CookieKeys.UNLOCK)

  if (!unlockedProducerId) {
    return true
  }

  return producer.id !== String(unlockedProducerId)
}

export const isSoldOutWithCapacity = (producer: Producer, capacity?: number): boolean => {
  // assume 3500 kWh
  const volume = capacity || AVERAGE_CAPACITY_USAGE
  const used = Math.round(((producer.energyUsed + volume) / producer.maxCapacity) * 100)

  return used >= 100
}

/**
 * Filters and sorts producers based on being enabled, popularity, capacity and order
 *
 * @param {FilterSortProducers} params
 * @returns {Producer[]}
 */
export const filterAndSortProducers = ({ producers, language, hideSoldOut, capacity }: FilterSortProducers): Producer[] => {
  const popularProducers = producers.filter((p) => p.isPopular[language] && !isSoldOutWithCapacity(p, capacity))

  const soldOutProducers = hideSoldOut
    ? []
    : producers.filter((p) => isSoldOutWithCapacity(p, capacity)).sort((p1, p2) => p1.currentCapacity - p2.currentCapacity)

  const otherProducers = producers
    .filter((p) => !isSoldOutWithCapacity(p, capacity) && !p.isPopular[language])
    .sort((p1: Producer, p2: Producer) => {
      return p1.order < p2.order ? -1 : p1.order > p2.order ? 1 : 0
    })

  return [...popularProducers, ...otherProducers, ...soldOutProducers]
}

/**
 * Returns the location of a producer in the correct language or fallback to a filled in language
 * @param {string} locale
 * @param {ProducerLocation['location']} location
 * @returns {string}
 */
export const getProducerLocation = (locale: string, location?: ProducerLocation['location']): string => {
  if (!location) return ''
  return location[locale] || location.nl || location.fr
}
